import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Container,
  Paper,
  Typography,
  TextField,
  Button,
  Grid,
  Box,
  Card,
  CardMedia,
  CardContent,
  IconButton,
  Slider,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions
} from '@mui/material';
import {
  Edit,
  PhotoCamera,
  Delete,
  Visibility
} from '@mui/icons-material';
import { GoogleMap, Marker, InfoWindow, useJsApiLoader } from '@react-google-maps/api';
import PlacesAutocomplete, { geocodeByAddress, getLatLng } from 'react-places-autocomplete';
import axios from '../utils/axios';

const ListGym = () => {
  console.log('ListGym component loaded - version with fixes');
  const navigate = useNavigate();
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);
  const [fieldErrors, setFieldErrors] = useState({});
  const [previewOpen, setPreviewOpen] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    pricePerHour: '',
    maxPeople: '',
    address: '',
    personalTrainingPremium: '',
    location: {
      lat: 0,
      lng: 0
    },
    photos: [],
    amenities: {
      parking: false,
      showers: false,
      wifi: false,
      personalTraining: false,
      refreshments: false,
      towelService: false,
      saunaSteamRoom: false
    }
  });

  // Google Maps API loader
  const { isLoaded, loadError } = useJsApiLoader({
    id: 'google-map-script',
    googleMapsApiKey: process.env.REACT_APP_GOOGLE_MAPS_API_KEY,
    libraries: ['places', 'geometry']
  });

  // Blue circle marker for gym
  const gymMarkerIcon = {
    path: 'M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 18c-4.41 0-8-3.59-8-8s3.59-8 8-8 8 3.59 8 8-3.59 8-8 8z',
    fillColor: '#1976d2',
    fillOpacity: 1,
    strokeColor: '#FFFFFF',
    strokeWeight: 3,
    scale: 1.8,
    anchor: { x: 12, y: 12 }
  };

  const mapOptions = {
    zoom: 18,
    mapTypeControl: true,
    streetViewControl: true,
    fullscreenControl: true,
    zoomControl: true,
    gestureHandling: 'cooperative',
    styles: [
      {
        featureType: 'poi',
        elementType: 'labels',
        stylers: [{ visibility: 'on' }]
      },
      {
        featureType: 'poi.business',
        elementType: 'labels',
        stylers: [{ visibility: 'on' }]
      },
      // Hide other business labels to make our gym stand out
      {
        featureType: 'poi.business',
        elementType: 'labels.text',
        stylers: [{ visibility: 'off' }]
      }
    ]
  };

  const [selectedFile, setSelectedFile] = useState(null);
  const [previewUrls, setPreviewUrls] = useState([]);
  const [isPreviewMode, setIsPreviewMode] = useState(false);
  const [isListed, setIsListed] = useState(false);
  const [draggedItem, setDraggedItem] = useState(null);
  const [isDraggingOverMain, setIsDraggingOverMain] = useState(false);
  const [suggestionsLoading, setSuggestionsLoading] = useState(false);
  const [showMapPreview, setShowMapPreview] = useState(false);
  const [googleReady, setGoogleReady] = useState(false);

  // Handle address selection from autocomplete
  const handleAddressSelect = async (address) => {
    try {
      setSuggestionsLoading(true);
      setShowMapPreview(false);
      const results = await geocodeByAddress(address);
      const latLng = await getLatLng(results[0]);
      
      console.log('Setting address in form data:', address);
      setFormData(prev => ({
        ...prev,
        address,
        location: latLng
      }));
      setError(null);
      setShowMapPreview(true); // Show map after geocoding
    } catch (error) {
      console.error('Error getting address details:', error);
      setError('Could not find location for this address. Please enter a valid address.');
      setShowMapPreview(false);
    } finally {
      setSuggestionsLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleFileChange = (e) => {
    const files = Array.from(e.target.files);
    if (files.length > 0) {
      // Validate each file
      const validFiles = files.filter(file => {
        // Validate file size (5MB limit)
        if (file.size > 5 * 1024 * 1024) {
          setError('File size must be less than 5MB');
          return false;
        }
        
        // Validate file type
        if (!['image/jpeg', 'image/png', 'image/jpg'].includes(file.type)) {
          setError('Only JPEG and PNG files are allowed');
          return false;
        }
        return true;
      });

      if (validFiles.length === 0) return;

      // Create preview URLs for all valid files
      validFiles.forEach(file => {
        const reader = new FileReader();
        reader.onloadend = () => {
          setPreviewUrls(prev => [...prev, reader.result]);
        };
        reader.readAsDataURL(file);
      });

      setSelectedFile(validFiles[0]); // Keep the first file as selected for form submission
      setError(null);
    }
  };

  const handleAmenityChange = (e) => {
    const { name, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      amenities: {
        ...prev.amenities,
        [name]: checked
      }
    }));
  };

  const handlePreview = () => {
    setError(null);
    setFieldErrors({});
    
    // Validate form data with specific error messages
    const missingFields = [];
    
    if (!formData.name?.trim()) missingFields.push('Name');
    if (!formData.description?.trim()) missingFields.push('Description');
    if (!formData.pricePerHour || isNaN(parseFloat(formData.pricePerHour)) || parseFloat(formData.pricePerHour) <= 0) missingFields.push('Price per hour');
    if (!formData.maxPeople || isNaN(parseInt(formData.maxPeople)) || parseInt(formData.maxPeople) < 1) missingFields.push('Maximum number of people');
    if (!formData.address?.trim()) missingFields.push('Address');

    if (missingFields.length > 0) {
      console.log('Missing fields for preview:', missingFields);
      setError(`Please fill in the following required fields before previewing: ${missingFields.join(', ')}`);
      return;
    }

    // Validate location
    if (!formData.location || typeof formData.location.lat !== 'number' || typeof formData.location.lng !== 'number') {
      setError('Please select a valid location before previewing');
      return;
    }

    // Validate personal training premium if personal training is enabled
    if (formData.amenities.personalTraining && (!formData.personalTrainingPremium || isNaN(parseFloat(formData.personalTrainingPremium)))) {
      setError('Please enter a valid personal training premium amount before previewing');
      return;
    }

    // If all validation passes, show preview
    setPreviewOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    setFieldErrors({});
    
    try {
      console.log('Starting form submission...');
      
      // Validate form data with specific error messages
      const missingFields = [];
      
      if (!formData.name?.trim()) missingFields.push('Name');
      if (!formData.description?.trim()) missingFields.push('Description');
      if (!formData.pricePerHour || isNaN(parseFloat(formData.pricePerHour)) || parseFloat(formData.pricePerHour) <= 0) missingFields.push('Price per hour');
      if (!formData.maxPeople || isNaN(parseInt(formData.maxPeople)) || parseInt(formData.maxPeople) < 1) missingFields.push('Maximum number of people');
      if (!formData.address?.trim()) missingFields.push('Address');

      if (missingFields.length > 0) {
        console.log('Missing fields:', missingFields);
        setError(`Please fill in the following required fields: ${missingFields.join(', ')}`);
        setLoading(false);
        return;
      }

      // Validate location
      if (!formData.location || typeof formData.location.lat !== 'number' || typeof formData.location.lng !== 'number') {
        setError('Please select a valid location');
        return;
      }

      // Validate personal training premium if personal training is enabled
      if (formData.amenities.personalTraining && (!formData.personalTrainingPremium || isNaN(parseFloat(formData.personalTrainingPremium)))) {
        setError('Please enter a valid personal training premium amount');
        return;
      }

      const formDataObj = new FormData();
      
      // Append text fields
      formDataObj.append('name', formData.name);
      formDataObj.append('description', formData.description);
      formDataObj.append('pricePerHour', formData.pricePerHour);
      formDataObj.append('maxPeople', formData.maxPeople);
      console.log('Adding address to FormData:', formData.address);
      formDataObj.append('address', formData.address || '');
      
      // Always append personal training premium
      console.log('Personal training premium value:', formData.personalTrainingPremium);
      formDataObj.append('personalTrainingPremium', formData.personalTrainingPremium || '0');
      
      formDataObj.append('status', 'draft');
      
      // Append location as JSON string
      formDataObj.append('location', JSON.stringify({
        lat: formData.location.lat,
        lng: formData.location.lng
      }));
      
      // Append amenities as JSON string
      formDataObj.append('amenities', JSON.stringify(formData.amenities));
      
      // Append photos
      if (selectedFile) {
        console.log('Appending photo:', selectedFile.name, selectedFile.type, selectedFile.size);
        formDataObj.append('photos', selectedFile);
      } else {
        console.log('No photo selected');
      }
      
      const token = localStorage.getItem('token');
      if (!token) {
        setError('You must be logged in to create a gym listing');
        return;
      }

      console.log('Form data prepared:', {
        name: formData.name,
        description: formData.description,
        pricePerHour: formData.pricePerHour,
        maxPeople: formData.maxPeople,
        address: formData.address,
        location: formData.location,
        amenities: formData.amenities,
        hasPhoto: !!selectedFile,
        token: token ? 'Present' : 'Missing'
      });
      
      // Debug FormData contents
      console.log('FormData entries:');
      for (let [key, value] of formDataObj.entries()) {
        console.log(`${key}:`, value);
      }

      console.log('Sending request to server...');
      const response = await axios.post('/api/gyms', formDataObj, {
        headers: {
          'Content-Type': 'multipart/form-data',
          'Authorization': `Bearer ${token}`
        }
      });
      
      console.log('Server response:', response.data);
      setIsPreviewMode(true);
      setError(null);
    } catch (err) {
      console.error('Error details:', {
        message: err.message,
        response: err.response?.data,
        status: err.response?.status,
        headers: err.response?.headers
      });
      
      const errorMessage = err.response?.data?.error || err.message || 'Failed to save gym data';
      setError(errorMessage);
      if (err.response?.status === 400 && err.response?.data?.details) {
        // Map backend field names to frontend field names if needed
        const details = err.response.data.details;
        const newFieldErrors = {};
        details.forEach(field => {
          if (field.includes('name')) newFieldErrors.name = 'Name is required';
          if (field.includes('description')) newFieldErrors.description = 'Description is required';
          if (field.includes('pricePerHour') || field.includes('price')) newFieldErrors.pricePerHour = 'Hourly rate is required';
          if (field.includes('maxPeople')) newFieldErrors.maxPeople = 'Max people is required';
          if (field.includes('address')) newFieldErrors.address = 'Address is required';
          if (field.includes('location')) newFieldErrors.location = 'Location is required';
          if (field.includes('amenities')) newFieldErrors.amenities = 'Select at least one amenity';
          if (field.includes('photos')) newFieldErrors.photos = 'At least one photo is required';
        });
        setFieldErrors(newFieldErrors);
        setError('Please fill in all required fields.');
        return;
      }
    } finally {
      setLoading(false);
    }
  };

  const handleListGym = async () => {
    setLoading(true);
    setError(null);
    setFieldErrors({});
    
    try {
      // Validate form data with specific error messages
      const missingFields = [];
      
      if (!formData.name?.trim()) missingFields.push('Name');
      if (!formData.description?.trim()) missingFields.push('Description');
      if (!formData.pricePerHour || isNaN(parseFloat(formData.pricePerHour)) || parseFloat(formData.pricePerHour) <= 0) missingFields.push('Price per hour');
      if (!formData.maxPeople || isNaN(parseInt(formData.maxPeople)) || parseInt(formData.maxPeople) < 1) missingFields.push('Maximum number of people');
      if (!formData.address?.trim()) missingFields.push('Address');

      if (missingFields.length > 0) {
        console.log('Missing fields:', missingFields);
        setError(`Please fill in the following required fields: ${missingFields.join(', ')}`);
        setLoading(false);
        return;
      }

      // Validate location
      if (!formData.location || typeof formData.location.lat !== 'number' || typeof formData.location.lng !== 'number') {
        setError('Please select a valid location');
        setLoading(false);
        return;
      }

      // Validate personal training premium if personal training is enabled
      if (formData.amenities.personalTraining && (!formData.personalTrainingPremium || isNaN(parseFloat(formData.personalTrainingPremium)))) {
        setError('Please enter a valid personal training premium amount');
        setLoading(false);
        return;
      }

      const formDataObj = new FormData();
      
      // Append text fields
      formDataObj.append('name', formData.name);
      formDataObj.append('description', formData.description);
      formDataObj.append('pricePerHour', formData.pricePerHour);
      formDataObj.append('maxPeople', formData.maxPeople);
      console.log('Adding address to FormData:', formData.address);
      formDataObj.append('address', formData.address || '');
      
      // Always append personal training premium
      console.log('Personal training premium value:', formData.personalTrainingPremium);
      formDataObj.append('personalTrainingPremium', formData.personalTrainingPremium || '0');
      
      formDataObj.append('status', 'listed');
      
      // Append location as JSON string
      formDataObj.append('location', JSON.stringify({
        lat: formData.location.lat,
        lng: formData.location.lng
      }));
      
      // Append amenities as JSON string
      formDataObj.append('amenities', JSON.stringify(formData.amenities));
      
      // Append photos
      if (selectedFile) {
        formDataObj.append('photos', selectedFile);
      }
      
      const token = localStorage.getItem('token');
      if (!token) {
        setError('You must be logged in to list a gym');
        return;
      }

      await axios.post('/api/gyms', formDataObj, {
        headers: {
          'Content-Type': 'multipart/form-data',
          'Authorization': `Bearer ${token}`
        }
      });
      
      setIsListed(true);
      navigate('/gyms');
    } catch (err) {
      console.error('Error listing gym:', err);
      const errorMessage = err.response?.data?.error || err.message || 'Failed to list gym';
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleDragStart = (index) => {
    setDraggedItem(index);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
  };

  const handleDragOverMain = (e) => {
    e.preventDefault();
    setIsDraggingOverMain(true);
  };

  const handleDragLeaveMain = () => {
    setIsDraggingOverMain(false);
  };

  const handleDrop = (dropIndex) => {
    if (draggedItem === null) return;
    
    const newPreviewUrls = [...previewUrls];
    const draggedUrl = newPreviewUrls[draggedItem];
    newPreviewUrls.splice(draggedItem, 1);
    newPreviewUrls.splice(dropIndex, 0, draggedUrl);
    
    setPreviewUrls(newPreviewUrls);
    setDraggedItem(null);
    setIsDraggingOverMain(false);
  };

  const handleDropMain = () => {
    if (draggedItem === null) return;
    
    const newPreviewUrls = [...previewUrls];
    const draggedUrl = newPreviewUrls[draggedItem];
    newPreviewUrls.splice(draggedItem, 1);
    newPreviewUrls.unshift(draggedUrl);
    
    setPreviewUrls(newPreviewUrls);
    setDraggedItem(null);
    setIsDraggingOverMain(false);
  };

  // Enhanced Google Maps loading with retry mechanism
  useEffect(() => {
    const checkGoogleReady = () => {
      if (isLoaded && window.google && window.google.maps && window.google.maps.places) {
        setGoogleReady(true);
        return true;
      }
      return false;
    };

    if (loadError) {
      console.error('Google Maps API load error:', loadError);
      setError('Failed to load Google Maps. Please check your API key.');
    } else if (isLoaded) {
      // Initial check
      if (checkGoogleReady()) return;

      // Setup retry mechanism if places library not immediately available
      const maxAttempts = 5;
      let attempts = 0;
      
      const intervalId = setInterval(() => {
        attempts++;
        if (checkGoogleReady() || attempts >= maxAttempts) {
          clearInterval(intervalId);
          if (attempts >= maxAttempts) {
            setError('Google Maps Places library failed to load. Please refresh the page.');
          }
        }
      }, 500);
      
      return () => clearInterval(intervalId);
    }
  }, [isLoaded, loadError]);

  const renderPreview = () => (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 3 }}>
        <Typography variant="h4" component="h1">
          {formData.name}
        </Typography>
        <Box>
          <Button
            startIcon={<Edit />}
            onClick={() => setIsPreviewMode(false)}
            sx={{ mr: 1 }}
          >
            Edit
          </Button>
          {!isListed && (
            <Button
              variant="contained"
              color="primary"
              onClick={handleListGym}
              disabled={loading}
            >
              {loading ? 'Listing...' : 'List This Gym'}
            </Button>
          )}
        </Box>
      </Box>

      <Grid container spacing={3}>
        <Grid item xs={12} md={8}>
          <Card>
            <Box
              onDragOver={handleDragOverMain}
              onDragLeave={handleDragLeaveMain}
              onDrop={handleDropMain}
              sx={{
                position: 'relative',
                border: isDraggingOverMain ? '2px dashed #1976d2' : 'none',
                transition: 'border 0.2s ease-in-out',
              }}
            >
              <CardMedia
                component="img"
                height="400"
                image={previewUrls[0] || 'https://via.placeholder.com/800x400?text=No+Image+Available'}
                alt={formData.name}
              />
              {isDraggingOverMain && (
                <Box
                  sx={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    bgcolor: 'rgba(25, 118, 210, 0.1)',
                  }}
                >
                  <Typography variant="h6" color="primary">
                    Drop to set as main image
                  </Typography>
                </Box>
              )}
            </Box>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Description
              </Typography>
              <Typography paragraph>
                {formData.description}
              </Typography>
              
              <Typography variant="h6" gutterBottom>
                Details
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <Typography variant="subtitle1">
                    Hourly Rate: ${formData.pricePerHour}
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="subtitle1">
                    Max People: {formData.maxPeople}
                  </Typography>
                </Grid>
              </Grid>

              <Typography variant="h6" gutterBottom>
                Amenities
              </Typography>
              <Grid container spacing={1}>
                {Object.entries(formData.amenities).map(([key, value]) => (
                  value && (
                    <Grid item key={key}>
                      <Typography variant="body2">{key.charAt(0).toUpperCase() + key.slice(1)}</Typography>
                    </Grid>
                  )
                ))}
                {Object.values(formData.amenities).every(v => !v) && (
                  <Typography variant="body2" color="text.secondary">No amenities selected</Typography>
                )}
              </Grid>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={4}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Photo Gallery
              </Typography>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                Drag photos to reorder or drop on the main image area to set as main image.
              </Typography>
              <Grid container spacing={1}>
                {previewUrls.map((url, index) => (
                  <Grid item xs={6} key={index}>
                    <Card
                      draggable
                      onDragStart={() => handleDragStart(index)}
                      onDragOver={handleDragOver}
                      onDrop={() => handleDrop(index)}
                      sx={{
                        cursor: 'move',
                        position: 'relative',
                        '&:hover': {
                          opacity: 0.9,
                        },
                        border: index === 0 ? '2px solid #1976d2' : 'none',
                        opacity: draggedItem === index ? 0.5 : 1,
                      }}
                    >
                      <CardMedia
                        component="img"
                        height="140"
                        image={url}
                        alt={`Gym photo ${index + 1}`}
                      />
                      {index === 0 && (
                        <Box
                          sx={{
                            position: 'absolute',
                            top: 4,
                            left: 4,
                            bgcolor: 'primary.main',
                            color: 'white',
                            px: 1,
                            py: 0.5,
                            borderRadius: 1,
                            fontSize: '0.75rem',
                          }}
                        >
                          Main Image
                        </Box>
                      )}
                    </Card>
                  </Grid>
                ))}
              </Grid>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );

  const renderForm = () => (
    <Box component="form" onSubmit={handleSubmit} sx={{ mt: 3 }}>
      <Grid container spacing={3}>
        <Grid item xs={12}>
          <Typography variant="h6" gutterBottom>
            Gym Details
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <TextField
                required
                fullWidth
                label="Gym Name"
                name="name"
                value={formData.name}
                onChange={handleInputChange}
                error={!!fieldErrors.name}
                helperText={fieldErrors.name}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                required
                fullWidth
                multiline
                rows={4}
                label="Description"
                name="description"
                value={formData.description}
                onChange={handleInputChange}
                error={!!fieldErrors.description}
                helperText={fieldErrors.description}
              />
            </Grid>
          </Grid>
        </Grid>

        <Grid item xs={12}>
          <Typography variant="h6" gutterBottom>
            Photos
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <input
                accept="image/*"
                id="photo-upload"
                type="file"
                multiple
                onChange={handleFileChange}
                style={{ display: 'none' }}
              />
              <label htmlFor="photo-upload">
                <Button variant="contained" component="span" startIcon={<PhotoCamera />}>
                  Upload Photo
                </Button>
              </label>
              {fieldErrors.photos && (
                <Typography color="error" variant="body2">{fieldErrors.photos}</Typography>
              )}
              <Typography variant="caption" display="block" color="text.secondary" sx={{ mt: 1 }}>
                You can select multiple photos at once. Each photo must be less than 5MB.
              </Typography>
            </Grid>
            {previewUrls.length > 0 && (
              <Grid item xs={12}>
                <Typography variant="subtitle2" gutterBottom>
                  Uploaded Photos:
                </Typography>
                <Grid container spacing={1}>
                  {previewUrls.map((url, index) => (
                    <Grid item xs={4} sm={3} md={2} key={index}>
                      <Card sx={{ position: 'relative' }}>
                        <CardMedia
                          component="img"
                          height="140"
                          image={url}
                          alt={`Preview ${index + 1}`}
                        />
                        <IconButton
                          size="small"
                          sx={{
                            position: 'absolute',
                            top: 4,
                            right: 4,
                            bgcolor: 'rgba(255, 255, 255, 0.8)',
                            '&:hover': {
                              bgcolor: 'rgba(255, 255, 255, 0.9)',
                            },
                          }}
                          onClick={() => {
                            setPreviewUrls(prev => prev.filter((_, i) => i !== index));
                            setSelectedFile(null);
                          }}
                        >
                          <Delete fontSize="small" />
                        </IconButton>
                      </Card>
                    </Grid>
                  ))}
                </Grid>
              </Grid>
            )}
          </Grid>
        </Grid>

        <Grid item xs={12}>
          <Typography variant="h6" gutterBottom>
            Options
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <Typography gutterBottom>
                Maximum Number of People
              </Typography>
              <Slider
                value={formData.maxPeople}
                onChange={(e, newValue) => {
                  setFormData(prev => ({
                    ...prev,
                    maxPeople: newValue
                  }));
                }}
                min={1}
                max={50}
                marks
                valueLabelDisplay="auto"
                sx={fieldErrors.maxPeople ? { border: '2px solid red', borderRadius: 2, px: 1 } : {}}
              />
              {fieldErrors.maxPeople && (
                <Typography color="error" variant="body2">{fieldErrors.maxPeople}</Typography>
              )}
            </Grid>
            <Grid item xs={12} md={6}>
              <Typography gutterBottom>
                Hourly Rate ($)
              </Typography>
              <TextField
                required
                fullWidth
                type="number"
                name="pricePerHour"
                value={formData.pricePerHour}
                onChange={handleInputChange}
                InputProps={{
                  startAdornment: '$',
                }}
                error={!!fieldErrors.pricePerHour}
                helperText={fieldErrors.pricePerHour}
              />
            </Grid>
          </Grid>
        </Grid>

        <Grid item xs={12}>
          <Typography variant="h6" gutterBottom>
            Amenities
          </Typography>
          <Grid container spacing={2}>
            {Object.keys(formData.amenities).map((key) => (
              <Grid item xs={6} sm={4} md={3} key={key}>
                <label>
                  <input
                    type="checkbox"
                    name={key}
                    checked={formData.amenities[key]}
                    onChange={handleAmenityChange}
                    style={{ marginRight: 8, outline: fieldErrors.amenities ? '2px solid red' : undefined }}
                  />
                  {key.charAt(0).toUpperCase() + key.slice(1)}
                </label>
              </Grid>
            ))}
          </Grid>
          {fieldErrors.amenities && (
            <Typography color="error" variant="body2">{fieldErrors.amenities}</Typography>
          )}
        </Grid>

        <Grid item xs={12}>
          <Typography variant="h6" gutterBottom>
            Location
          </Typography>
          
          {/* Address Input with Google Places Autocomplete */}
          {googleReady ? (
            <PlacesAutocomplete
              value={formData.address}
              onChange={(address) => setFormData(prev => ({ ...prev, address }))}
              onSelect={handleAddressSelect}
            >
              {({ getInputProps, suggestions, getSuggestionItemProps }) => (
                <div>
                  <TextField
                    {...getInputProps({
                      fullWidth: true,
                      label: 'Address*',
                      placeholder: 'Enter the full address of your gym',
                      required: true,
                      margin: 'normal',
                      name: 'address',
                      error: !!fieldErrors.address,
                      helperText: fieldErrors.address || 'Start typing and select an address from the suggestions',
                    })}
                  />
                  <Box sx={{ position: 'relative' }}>
                    <Box sx={{
                      position: 'absolute',
                      zIndex: 1000,
                      width: '100%',
                      bgcolor: 'background.paper',
                      boxShadow: 3,
                      borderRadius: 1,
                      mt: 0.5,
                      maxHeight: 200,
                      overflowY: 'auto'
                    }}>
                      {suggestionsLoading && <Box sx={{ p: 2 }}>Loading addresses...</Box>}
                      {suggestions.map(suggestion => {
                        const style = {
                          backgroundColor: suggestion.active ? '#f0f0f0' : '#fff',
                          cursor: 'pointer',
                          padding: '10px',
                          borderBottom: '1px solid #eee'
                        };
                        return (
                          <div
                            {...getSuggestionItemProps(suggestion, { style })}
                            key={suggestion.placeId}
                          >
                            {suggestion.description}
                          </div>
                        );
                      })}
                    </Box>
                  </Box>
                </div>
              )}
            </PlacesAutocomplete>
          ) : (
            <TextField
              fullWidth
              label="Address*"
              value={formData.address}
              onChange={(e) => setFormData(prev => ({ ...prev, address: e.target.value }))}
              placeholder="Enter the full address of your gym"
              helperText="Loading Google Maps..."
              required
              name="address"
              disabled={!isLoaded}
              error={!!fieldErrors.address}
            />
          )}
          
          {/* Map Preview */}
          {showMapPreview && (
            <Box sx={{ mt: 2, mb: 2 }}>
              <Typography variant="subtitle1" gutterBottom>
                Location Preview
              </Typography>
              <Box sx={{ height: 350, width: '100%', borderRadius: 1, overflow: 'hidden', border: '2px solid #e0e0e0' }}>
                {isLoaded && formData.location.lat ? (
                  <GoogleMap
                    mapContainerStyle={{ width: '100%', height: '100%' }}
                    center={formData.location}
                    zoom={18}
                    options={mapOptions}
                  >
                    {/* Blue circle marker */}
                    <Marker
                      position={formData.location}
                      title={formData.name}
                      icon={gymMarkerIcon}
                    />
                    
                    {/* Blue-themed gym name overlay */}
                    <InfoWindow
                      position={{
                        lat: formData.location.lat + 0.0001,
                        lng: formData.location.lng
                      }}
                      options={{
                        disableAutoPan: false,
                        closeBoxURL: "", // Remove close button so it stays visible
                        enableEventPropagation: true
                      }}
                    >
                      <Box sx={{ 
                        p: 1.5, 
                        minWidth: 200,
                        maxWidth: 300,
                        textAlign: 'center',
                        background: 'linear-gradient(135deg, #1976d2 0%, #42a5f5 100%)',
                        borderRadius: '8px',
                        color: 'white',
                        boxShadow: '0 4px 12px rgba(25, 118, 210, 0.3)'
                      }}>
                        <Typography 
                          variant="h6" 
                          sx={{ 
                            fontWeight: 'bold',
                            fontSize: '1.2rem',
                            textShadow: '1px 1px 2px rgba(0,0,0,0.3)',
                            mb: 0.5,
                            lineHeight: 1.2
                          }}
                        >
                          🏋️ {formData.name || 'Your Gym'}
                        </Typography>
                        <Typography 
                          variant="body2" 
                          sx={{ 
                            fontSize: '0.9rem',
                            opacity: 0.95,
                            fontWeight: 500,
                            mt: 0.5
                          }}
                        >
                         
                        </Typography>
                      </Box>
                    </InfoWindow>
                  </GoogleMap>
                ) : (
                  <Box sx={{ 
                    height: '100%', 
                    display: 'flex', 
                    alignItems: 'center', 
                    justifyContent: 'center',
                    bgcolor: '#f5f5f5'
                  }}>
                    <CircularProgress />
                    <Typography variant="body1" sx={{ ml: 2 }}>Loading map...</Typography>
                  </Box>
                )}
              </Box>
            </Box>
          )}
        </Grid>
        
        {/* Personal Training Premium - only show when personal training is enabled */}
        {/* Debug: personalTraining = ${formData.amenities.personalTraining} */}
        {formData.amenities.personalTraining && (
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Personal Training Premium (per hour)"
              name="personalTrainingPremium"
              type="number"
              value={formData.personalTrainingPremium}
              onChange={handleInputChange}
              InputProps={{
                startAdornment: '$',
              }}
              helperText="Set the premium price for personal training (required when personal training is enabled)"
              required
            />
          </Grid>
        )}
        
        {/* Location error message */}
        {fieldErrors.location && (
          <Grid item xs={12}>
            <Typography color="error" variant="body2">{fieldErrors.location}</Typography>
          </Grid>
        )}
        {error && (
          <Grid item xs={12}>
            <Typography color="error">{error}</Typography>
          </Grid>
        )}

        <Grid item xs={12}>
          <Button
            variant="contained"
            color="primary"
            size="large"
            fullWidth
            sx={{ mt: 2, mb: 1 }}
            startIcon={<Visibility />}
            onClick={handlePreview}
          >
            Preview Listing
          </Button>
        </Grid>

        <Grid item xs={12}>
          <Button
            type="submit"
            variant="contained"
            color="secondary"
            size="large"
            fullWidth
            sx={{ mt: 1, mb: 2 }}
            disabled={loading}
          >
            {loading ? 'Creating...' : 'Create Listing'}
          </Button>
        </Grid>
      </Grid>
    </Box>
  );

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Paper elevation={3} sx={{ p: 4 }}>
        {isPreviewMode ? renderPreview() : renderForm()}
      </Paper>
      
      {/* Preview Dialog */}
      <Dialog
        open={previewOpen}
        onClose={() => setPreviewOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Preview Listing</DialogTitle>
        <DialogContent>
          {renderPreview()}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPreviewOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default ListGym;
